import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { ProjectManagementJobResponse } from 'types/ProjectTypes';
import { buildQueryString } from 'utils/url';

import { APIResponse, BUSMAN_TAGS, busmanApiSlice } from './busmanApiSlice';
import { getApiErrorMessage } from './errors';
import {
  GetClientJobsResponse,
  GetCommentResponse,
  GetJobAcitivityBreakdownResponse,
  GetJobEstimationResponse,
  GetJobResponse,
} from './types';

export const jobEndpoints = busmanApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getActivityBreakdownByJob: builder.query({
      query: ({ jobID, startDate, endDate }) =>
        `/clients/jobs/activitybreakdown?job_id=${jobID}&start_date=${startDate}&end_date=${endDate}`,
      transformResponse: (response: GetJobAcitivityBreakdownResponse) => response.data,
    }),

    getActiveJobs: builder.query({
      query: ({ isActive = 'true' }) => `client/jobs/?is_active=${isActive}`,
      transformResponse: (response: GetClientJobsResponse) => response.data,
      providesTags: [BUSMAN_TAGS.Job],
    }),

    getAllJobs: builder.query({
      query: () => 'client/jobs/',
      transformResponse: (response: GetClientJobsResponse) => response.data,
      providesTags: [BUSMAN_TAGS.Job],
    }),

    getJobByProjectId: builder.query({
      query: ({ clientId, projectId, projectScope, isActive = true }) =>
        `/client/jobs/${buildQueryString({
          client_id: clientId,
          project_id: projectId,
          is_active: isActive,
          project_scope: projectScope,
        })}`,
      providesTags: [BUSMAN_TAGS.Job],
      transformResponse: (response: APIResponse<ProjectManagementJobResponse[]>) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    getJobsByClient: builder.query({
      query: (clientID) => `/client/jobs/?client_id=${clientID}&is_active=${true}`,
      transformResponse: (response: GetClientJobsResponse) => {
        return response.data;
      },
      providesTags: [BUSMAN_TAGS.Job],
    }),

    getJobById: builder.query({
      query: (id) => `/client/jobs/${id}`,
      transformResponse: (response: GetJobResponse) => response.data,
      providesTags: (result, error, { jobId }) => [{ type: BUSMAN_TAGS.Job, id: jobId }],
    }),

    addJob: builder.mutation({
      query: (newJob) => ({
        url: 'client/jobs/',
        method: 'post',
        body: newJob,
      }),
      invalidatesTags: [BUSMAN_TAGS.Job],
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    updateJob: builder.mutation({
      query: ({ clientId, jobUpdates }) => {
        return {
          url: `client/jobs/?client_id=${clientId}`,
          method: 'put',
          body: jobUpdates,
        };
      },
      invalidatesTags: (result, error, { jobId }) => [{ type: BUSMAN_TAGS.Job, id: jobId }, BUSMAN_TAGS.Quote],
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      // async onQueryStarted(args, { dispatch, queryFulfilled }) {
      //   try {
      //     const {
      //       data: { data: updatedJob },
      //     } = await queryFulfilled;
      //     if (updatedJob.is_active) {
      //       dispatch(
      //         jobEndpoints.util.updateQueryData('getActiveJobs', {}, (draft) => {
      //           const jobToUpdateIndex = draft.findIndex((jobItem) => jobItem.job_id === updatedJob.job_id);
      //           if (jobToUpdateIndex !== -1) {
      //             draft[jobToUpdateIndex] = updatedJob;
      //           }
      //           if (jobToUpdateIndex === -1) draft.push(updatedJob); // push new active job to the list
      //         }),
      //       );
      //     }

      //     if (!updatedJob.is_active) {
      //       // Removes job from the active joblist when made inactive
      //       dispatch(
      //         jobEndpoints.util.updateQueryData('getActiveJobs', {}, (draft) => {
      //           const jobToUpdateIndex = draft.findIndex((jobItem) => jobItem.job_id === updatedJob.job_id);
      //           if (jobToUpdateIndex !== -1) {
      //             draft.splice(jobToUpdateIndex, 1);
      //           }
      //         }),
      //       );
      //     }

      //     // Update all jobs list if present
      //     dispatch(
      //       jobEndpoints.util.updateQueryData('getAllJobs', {}, (draft) => {
      //         if (!draft.length) return;
      //         const jobToUpdateIndex = draft.findIndex((jobItem) => jobItem.job_id === updatedJob.job_id);
      //         if (jobToUpdateIndex !== -1) {
      //           draft[jobToUpdateIndex] = updatedJob;
      //         }
      //       }),
      //     );
      //   } catch (error) {
      //     console.log(error);
      //   }
      // },
    }),

    getJobComments: builder.query({
      query: ({ id, page = 1, limit = 5 }) => `/clients/jobs/comments?job_id=${id}&page=${page}&limit=${limit}`,
      transformResponse: (response: GetCommentResponse) => response.data,
      providesTags: [BUSMAN_TAGS.JobComments],
    }),

    addJobComment: builder.mutation({
      query: (newComment) => ({
        url: 'clients/jobs/comments',
        method: 'post',
        body: newComment,
      }),
      invalidatesTags: [BUSMAN_TAGS.JobComments],
    }),

    deleteJobComment: builder.mutation({
      query: (id) => ({
        url: 'clients/jobs/comments',
        method: 'delete',
        body: { comment_id: id },
      }),
      invalidatesTags: [BUSMAN_TAGS.JobComments],
    }),

    updateJobComment: builder.mutation({
      query: (updatedComment) => {
        return {
          url: 'clients/jobs/comments',
          method: 'put',
          body: updatedComment,
        };
      },
      invalidatesTags: [BUSMAN_TAGS.JobComments],
    }),
    getCostEstimationById: builder.query({
      query: (jobId) => `clients/jobs/costestimation?job_id=${jobId}`,
      transformResponse: (response: GetJobEstimationResponse) => {
        return response.data;
      },
      providesTags: [BUSMAN_TAGS.Job],
    }),
  }),
});

export const {
  useAddJobCommentMutation,
  useAddJobMutation,
  useDeleteJobCommentMutation,
  useGetActivityBreakdownByJobQuery,
  useGetActiveJobsQuery,
  useGetAllJobsQuery,
  useGetJobByIdQuery,
  useLazyGetJobByIdQuery,
  useGetJobCommentsQuery,
  useUpdateJobMutation,
  useUpdateJobCommentMutation,
  useLazyGetJobsByClientQuery,
  useGetJobsByClientQuery,
  useGetCostEstimationByIdQuery,
  useGetJobByProjectIdQuery,
} = jobEndpoints;
